// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for retrieving date inconsistencies in assets.
 *
 * Provides functionality to fetch assets with date inconsistencies such as
 * in-service dates before inventory dates, movements before inventory, etc.
 */
import { api } from '@/api/axios-instance';
import { responseMetadataSchema } from '@/api/types';
import { ExceptionType } from '@/constants/asset-exception-type';
import { dateInconsistencyItemSchema } from '@/report/api/types';
import { parseArrayInChunks } from '@/utilities/chunked-parse';
import queryString from 'query-string';
import { z } from 'zod';

export const dateInconsistenciesResponseSchema = z.object({
  data: z.object({
    assets: z.array(dateInconsistencyItemSchema),
    totalAnalyzed: z.number(),
  }),
  metadata: responseMetadataSchema,
});

type DateInconsistenciesResponse = z.infer<
  typeof dateInconsistenciesResponseSchema
>;

export interface DateInconsistenciesParameters {
  exceptionType?: ExceptionType;
  assetIds?: string[];
}

/**
 * Retrieves assets with date inconsistencies.
 *
 * @param params - The parameters for the request
 * @returns A promise that resolves to the date inconsistencies data
 */
export async function getDateInconsistencies(
  params?: DateInconsistenciesParameters
) {
  const path = '/v1/report/exception/asset/date';
  const response = await api.get<DateInconsistenciesResponse>(path, {
    paramsSerializer: (parameters) => queryString.stringify(parameters),
    params: {
      exceptionType: params?.exceptionType,
      assetIds: params?.assetIds,
    },
  });

  // Parse assets array in chunks to prevent UI thread blocking
  const assets = await parseArrayInChunks(
    response.data.data.assets,
    dateInconsistencyItemSchema
  );

  return {
    assets,
    totalAnalyzed: response.data.data.totalAnalyzed,
  };
}
